/**
 * Transforms a SysMLModel into TreeNode[] for the sysml-tree component.
 *
 * Organizes elements hierarchically: packages → element-type groups → elements.
 * Part definitions with sub-parts are rendered as expandable folders.
 */
import type { SysMLModel, Package, ElementBase, PartDefinition } from '@arctek/sysml-types';

export interface TreeNode {
  key: string;
  kind: 'folder' | 'file';
  id: string;
  name: string;
  depth: number;
  isOpen?: boolean;
  metaKind?: string | null;
  children?: TreeNode[];
  data?: Record<string, unknown>;
}

/** Element-type category definitions for grouping within a package */
const ELEMENT_GROUPS: ReadonlyArray<{
  label: string;
  key: string;
  pick: (pkg: Package) => ElementBase[];
}> = [
  { label: 'Parts', key: 'parts', pick: (p) => p.partDefs },
  { label: 'Actions', key: 'actions', pick: (p) => p.actionDefs },
  { label: 'Ports', key: 'ports', pick: (p) => p.portDefs },
  { label: 'Items', key: 'items', pick: (p) => p.itemDefs },
  { label: 'States', key: 'states', pick: (p) => p.stateDefs },
  { label: 'Requirements', key: 'reqs', pick: (p) => p.requirementDefs },
  { label: 'Constraints', key: 'constraints', pick: (p) => p.constraintDefs },
  { label: 'Connections', key: 'conns', pick: (p) => [...p.connectionDefs, ...p.connections, ...p.relationships.filter(r => r.relType === 'connection')] },
  { label: 'Allocations', key: 'allocs', pick: (p) => [...p.allocationDefs, ...p.allocations] },
  { label: 'Use Cases', key: 'usecases', pick: (p) => p.useCaseDefs },
  { label: 'Interfaces', key: 'ifaces', pick: (p) => p.interfaceDefs },
  { label: 'Verification', key: 'verif', pick: (p) => p.verificationDefs },
  { label: 'Analysis', key: 'analysis', pick: (p) => p.analysisDefs },
  { label: 'Calculations', key: 'calcs', pick: (p) => p.calcDefs },
  { label: 'Views', key: 'views', pick: (p) => [...p.viewDefs, ...p.viewpointDefs, ...p.renderingDefs] },
  { label: 'Metadata', key: 'metadata', pick: (p) => p.metadataDefs },
  { label: 'Occurrences', key: 'occurrences', pick: (p) => p.occurrenceDefs },
  { label: 'Enums', key: 'enums', pick: (p) => p.enums },
];

function buildPartDefNode(def: PartDefinition, depth: number): TreeNode {
  const children: TreeNode[] = [];
  const d = depth + 1;

  for (const part of def.parts) {
    children.push({
      key: part.id,
      kind: 'file',
      id: part.id,
      name: `${part.name}${part.typeRef && part.typeRef !== '?' ? ' : ' + part.typeRef : ''}`,
      depth: d,
      metaKind: part.elementType,
      data: { elementId: part.id },
    });
  }

  if (def.ports?.length) {
    for (const port of def.ports) {
      children.push({
        key: port.id,
        kind: 'file',
        id: port.id,
        name: `${port.name}${port.typeRef && port.typeRef !== '?' ? ' : ' + port.typeRef : ''}`,
        depth: d,
        metaKind: port.elementType,
        data: { elementId: port.id },
      });
    }
  }

  if (children.length === 0) {
    return {
      key: def.id,
      kind: 'file',
      id: def.id,
      name: def.name,
      depth,
      metaKind: def.elementType,
      data: { elementId: def.id },
    };
  }

  return {
    key: def.id,
    kind: 'folder',
    id: def.id,
    name: def.name,
    depth,
    isOpen: false,
    metaKind: def.elementType,
    children,
    data: { elementId: def.id },
  };
}

function buildPackageNode(pkg: Package, depth: number): TreeNode {
  const children: TreeNode[] = [];
  const groupDepth = depth + 1;

  for (const group of ELEMENT_GROUPS) {
    const elements = group.pick(pkg);
    if (elements.length === 0) continue;

    const groupId = `${pkg.id}::${group.key}`;
    const elementDepth = groupDepth + 1;

    const elementNodes: TreeNode[] = elements.map((el) => {
      if (el.elementType === 'partDef') {
        return buildPartDefNode(el as PartDefinition, elementDepth);
      }
      return {
        key: el.id,
        kind: 'file' as const,
        id: el.id,
        name: el.name,
        depth: elementDepth,
        metaKind: el.elementType,
        data: { elementId: el.id },
      };
    });

    children.push({
      key: groupId,
      kind: 'folder',
      id: groupId,
      name: `${group.label} (${elements.length})`,
      depth: groupDepth,
      isOpen: true,
      metaKind: group.key,
      children: elementNodes,
    });
  }

  // Nested packages
  for (const sub of pkg.packages) {
    children.push(buildPackageNode(sub, groupDepth));
  }

  return {
    key: pkg.id,
    kind: 'folder',
    id: pkg.id,
    name: pkg.name,
    depth,
    isOpen: true,
    metaKind: 'package',
    children,
    data: { elementId: pkg.id },
  };
}

/**
 * Convert a parsed SysMLModel into a TreeNode[] hierarchy for <sysml-tree>.
 */
export function modelToTreeNodes(model: SysMLModel | null | undefined): TreeNode[] {
  if (!model?.packages?.length) return [];
  return model.packages.map((pkg) => buildPackageNode(pkg, 0));
}
